import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {
  BokkzVpbDataService,
  CorporateTaxEntity,
  Investering,
  VpbAangifte
} from "@snelstart-ventures/fiscaal/domain-logic";
import {FormArray, FormControl, FormGroup} from "@angular/forms";
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {debounceTime, filter, Observable, shareReplay, Subscription, tap} from "rxjs";
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiDialogModule} from '@snelstart/ui-dialog';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

type InvesteringFormControls = {
  omschrijving: FormControl<string | null>;
  totaleInvesteringInBoekjaar: FormControl<number | null>;
  maximaleInvesteringBedragBedrijfsMiddel: FormControl<number | null>;
  datumIngebruikname: FormControl<Date | null>;
  bedragBetaaldInBoekjaar: FormControl<number | null>;
  meldingsNummer: FormControl<string | null>;
  miaLetter: FormControl<string | null>;
  claimKia: FormControl<boolean>;
  claimEia: FormControl<boolean>;
  claimMia: FormControl<boolean>;
};

type InvesteringFormGroup = FormGroup<InvesteringFormControls>;

@Component({
  selector: 'snelstart-venture-fiscaal-feature-investeringen',
  templateUrl: './investeringen.component.html',
  styleUrls: ['./investeringen.component.scss'],
  imports: [
    SharedUiFormControlsModule,
    SharedUiButtonModule,
    SharedUiDialogModule,
    SharedUiDetailsModule,
    CommonModule
  ]
})
export class InvesteringenComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzVpbDataService);
  private destroyRef = inject(DestroyRef);
  private investeringSubscriptions = new Subscription();
  public index = 0;
  public dialogVisible = false;

  readonly corporateTaxEntity$: Observable<CorporateTaxEntity> =
    this.dataService.corporateTaxEntity$.pipe(
      filter((data): data is CorporateTaxEntity => !!data?.sstRelationId),
      tap(data => {
        if (data.aangifte) {
          this.updateForm(data.aangifte);
        }
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  public investeringenFg = new FormGroup({
    investeringen: new FormArray<InvesteringFormGroup>([]),
  });

  public optionsJaNee: SelectFormControlItem<boolean>[] = [
    {value: true, label: "Ja"},
    {value: false, label: "Nee"},
  ]

  public miaLetter: SelectFormControlItem<string>[] = [
    {value: 'A', label: "A = 36% MIA + 75% Vamil"},
    {value: 'B', label: "B = 27% MIA + 75% Vamil"},
    {value: 'C', label: "C = alleen 75% Vamil"},
    {value: 'D', label: "D = alleen 36% MIA"},
    {value: 'E', label: "E = alleen 27% MIA"},
    {value: 'F', label: "F = 45% MIA + 75% Vamil"},
    {value: 'G', label: "G = alleen 45% MIA"}
  ];

  showDialog(index: number) {
    this.index = index;
    this.dialogVisible = true;
  }

  get investeringen(): FormArray<InvesteringFormGroup> {
    return this.investeringenFg.controls.investeringen;
  }

  addRij(): void {
    this.investeringen.push(this.createFormGroup());
    this.watchInvesteringChanges();
  }

  verwijderRij(index: number): void {
    this.investeringen.removeAt(index);
    this.saveForm();
    this.dataService.calculateLatestCorporateTaxReturn().subscribe();
    this.watchInvesteringChanges();
  }

  createFormGroup(item?: Investering): InvesteringFormGroup {
    return new FormGroup({
      omschrijving: new FormControl<string | null>(item?.omschrijving ?? null),
      totaleInvesteringInBoekjaar: new FormControl<number | null>(item?.totaleInvesteringInBoekjaar ?? null),
      maximaleInvesteringBedragBedrijfsMiddel: new FormControl<number | null>(item?.maximaleInvesteringBedragBedrijfsMiddel ?? null),
      datumIngebruikname: new FormControl<Date | null>(item?.datumIngebruikname ?? null),
      bedragBetaaldInBoekjaar: new FormControl<number | null>(item?.bedragBetaaldInBoekjaar ?? null),
      meldingsNummer: new FormControl<string | null>(item?.meldingsNummer ?? null),
      miaLetter: new FormControl<string | null>(item?.miaLetter ?? null),
      claimKia: new FormControl<boolean>(item?.claimKia ?? false, {nonNullable: true}),
      claimEia: new FormControl<boolean>(item?.claimEia ?? false, {nonNullable: true}),
      claimMia: new FormControl<boolean>(item?.claimMia ?? false, {nonNullable: true}),
    });
  }

  ngOnInit(): void {
    this.watchInvesteringChanges();
  }

  private watchInvesteringChanges(): void {
    this.investeringSubscriptions.unsubscribe();
    this.investeringSubscriptions = new Subscription();

    this.investeringen.controls.forEach(group => {
      this.subscribeToControlChanges(group.controls.omschrijving.valueChanges, () => {
        this.saveForm();
      });
      this.subscribeToControlChanges(group.controls.meldingsNummer.valueChanges, () => {
        this.saveForm();
      });
      this.subscribeToControlChanges(group.controls.datumIngebruikname.valueChanges, () => {
        this.saveForm();
      });

      this.subscribeToControlChanges(group.controls.totaleInvesteringInBoekjaar.valueChanges, () => {
        this.handleRecalculationForGroup(group);
      });
      this.subscribeToControlChanges(group.controls.maximaleInvesteringBedragBedrijfsMiddel.valueChanges, () => {
        this.handleRecalculationForGroup(group);
      });
      this.subscribeToControlChanges(group.controls.bedragBetaaldInBoekjaar.valueChanges, () => {
        this.handleRecalculationForGroup(group);
      });
      this.subscribeToControlChanges(group.controls.claimKia.valueChanges, () => {
        this.handleRecalculationForGroup(group);
      });
      this.subscribeToControlChanges(group.controls.claimEia.valueChanges, () => {
        this.handleRecalculationForGroup(group);
      });
      this.subscribeToControlChanges(group.controls.claimMia.valueChanges, () => {
        this.handleRecalculationForGroup(group);
      });
      this.subscribeToControlChanges(group.controls.miaLetter.valueChanges, () => {
        this.handleRecalculationForGroup(group);
      });
    });
  }

  private subscribeToControlChanges(valueChanges$: Observable<unknown>, callback: () => void): void {
    this.investeringSubscriptions.add(
      valueChanges$.pipe(
        debounceTime(300),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(callback)
    );
  }

  private handleRecalculationForGroup(group: InvesteringFormGroup): void {
    if (group.controls.claimEia.value) {
      group.controls.claimMia.setValue(false, {emitEvent: false});
      group.controls.miaLetter.setValue(null, {emitEvent: false});
    }
    if (group.controls.claimMia.value) {
      group.controls.claimEia.setValue(false, {emitEvent: false});
    }
    this.saveForm();
    this.dataService.calculateLatestCorporateTaxReturn().subscribe();
  }

  private updateForm(vpbAangifte: VpbAangifte): void {
    const lijst = vpbAangifte.specificatieInvesteringen?.investeringslijst || [];
    const formArray = this.investeringenFg.controls.investeringen;

    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1, {emitEvent: false});
    }

    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        const control = formArray.at(i);
        control.patchValue(lijst[i], {emitEvent: false});
        if (control.controls.claimEia.getRawValue()) {
          control.controls.claimMia.setValue(false, {emitEvent: false});
          control.controls.miaLetter.setValue(null, {emitEvent: false});
        }
        if (control.controls.claimMia.getRawValue()) {
          control.controls.claimEia.setValue(false, {emitEvent: false});
        }
      } else {
        formArray.push(this.createFormGroup(lijst[i]), {emitEvent: false});
      }
    }

    this.watchInvesteringChanges();
  }

  ngOnDestroy(): void {
    this.investeringSubscriptions.unsubscribe();
    this.saveForm();
  }

  saveForm(): void {
    this.investeringenFg.updateValueAndValidity({emitEvent: false});

    const investeringslijst: Investering[] = [];

    this.investeringenFg.getRawValue().investeringen.forEach(item => {
      const omschrijving = item.omschrijving?.trim();

      investeringslijst.push({
        omschrijving: omschrijving ?? '',
        totaleInvesteringInBoekjaar: item.totaleInvesteringInBoekjaar ?? 0,
        maximaleInvesteringBedragBedrijfsMiddel: item.maximaleInvesteringBedragBedrijfsMiddel ?? undefined,
        datumIngebruikname: item.datumIngebruikname ?? undefined,
        bedragBetaaldInBoekjaar: item.bedragBetaaldInBoekjaar ?? undefined,
        meldingsNummer: item.meldingsNummer?.trim() || undefined,
        miaLetter: item.miaLetter ?? undefined,
        claimKia: item.claimKia,
        claimEia: item.claimEia,
        claimMia: item.claimMia,
      });
    });

    this.dataService.patchCorporateTaxEntity(currentEntity => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }

      return {
        ...currentEntity,
        aangifte: {
          ...currentEntity.aangifte,
          specificatieInvesteringen: {
            ...currentEntity.aangifte.specificatieInvesteringen,
            investeringslijst,
          },
        },
      } as CorporateTaxEntity;
    });
  }
}
